import React, { useState, useEffect } from 'react';
import { Card, Table, Input, Select, Space, Typography, Tag, Row, Col, Statistic, Tooltip, Button } from 'antd';
import { SearchOutlined, BarChartOutlined, DatabaseOutlined, ToolOutlined, PlusOutlined } from '@ant-design/icons';
import { oemPartsApi } from '../../services/oemPartsApi';
import type { SharedCatalogPart, SharedCatalogStats } from '../../types/oem-parts';

const { Title, Text } = Typography;
const { Option } = Select;

const SharedPartsBrowser: React.FC = () => {
    const [parts, setParts] = useState<SharedCatalogPart[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<SharedCatalogStats | null>(null);

    // Filters
    const [search, setSearch] = useState('');
    const [make, setMake] = useState<string | undefined>(undefined);
    const [page, setPage] = useState(1);
    const limit = 50;

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        fetchParts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, make]);

    const fetchStats = async () => {
        try {
            const data = await oemPartsApi.getSharedCatalogStats();
            setStats(data);
        } catch (e) {
            console.error('Failed to fetch stats', e);
        }
    };

    const fetchParts = async () => {
        setLoading(true);
        try {
            if (search || make) {
                const { parts: p, total: t } = await oemPartsApi.searchSharedCatalog(search, { make, page, limit });
                setParts(p);
                setTotal(t);
            } else {
                const { parts: p, total: t } = await oemPartsApi.getSharedCatalog(page, limit);
                setParts(p);
                setTotal(t);
            }
        } catch (e) {
            console.error('Failed to fetch parts', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearch(value);
        setPage(1);
        // Directly fetch parts if triggering from search button
        oemPartsApi.searchSharedCatalog(value, { make, page: 1, limit }).then(({ parts: p, total: t }) => {
            setParts(p);
            setTotal(t);
        }).catch(console.error);
    };

    const formatPrice = (p: SharedCatalogPart) => {
        const price = p.retail_price ?? p.our_price ?? p.actual_price ?? p.msrp_price;
        return price != null ? `$${price.toFixed(2)}` : 'N/A';
    };

    const columns = [
        {
            title: 'Part Number',
            dataIndex: 'oem_part_number',
            key: 'oem_part_number',
            render: (text: string) => <Tag style={{ fontFamily: 'monospace' }}>{text}</Tag>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text: string, record: SharedCatalogPart) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{text}</Text>
                    {record.callout_number && <Text type="secondary" style={{ fontSize: 12 }}>Callout: #{record.callout_number}</Text>}
                </Space>
            ),
        },
        {
            title: 'Price',
            key: 'price',
            align: 'right' as const,
            width: 120,
            render: (_: any, record: SharedCatalogPart) => formatPrice(record),
        },
        {
            title: 'Confidence',
            dataIndex: 'confidence_level',
            key: 'confidence_level',
            render: (level: string) => {
                const colors = { high: 'green', medium: 'orange', low: 'red', not_confident: 'red' };
                return <Tag color={(colors as any)[level] || 'default'}>{level.replace('_', ' ').toUpperCase()}</Tag>;
            },
            width: 140,
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center' as const,
            width: 100,
            render: (_: any, record: SharedCatalogPart) => (
                <Tooltip title={record.is_permanent ? "Stored permanently in local catalog" : ""}>
                    <Button
                        type="text"
                        icon={<DatabaseOutlined style={{ color: record.is_permanent ? '#1890ff' : '#bfbfbf' }} />}
                    />
                </Tooltip>
            ),
        }
    ];

    return (
        <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
            <Space align="center" style={{ marginBottom: 16 }}>
                <DatabaseOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                <Title level={2} style={{ margin: 0 }}>Shared Parts Catalog</Title>
            </Space>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8} md={6}>
                    <Card size="small" style={{ height: '100%', borderColor: '#e6f7ff', backgroundColor: '#e6f7ff' }}>
                        <Statistic
                            title="Total Parts"
                            value={stats?.total_permanent_parts || 0}
                            prefix={<ToolOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                    <Card size="small" style={{ height: '100%', borderColor: '#f6ffed', backgroundColor: '#f6ffed' }}>
                        <Statistic
                            title="Total Diagrams"
                            value={stats?.total_permanent_diagrams || 0}
                            prefix={<BarChartOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                    <Card size="small" style={{ height: '100%', borderColor: '#fff0f6', backgroundColor: '#fff0f6' }}>
                        <Statistic
                            title="Interactive Diagrams"
                            value={stats?.diagrams_with_hotspots || 0}
                            prefix={<PlusOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Card size="small">
                <Space style={{ marginBottom: 16 }} wrap>
                    <Input.Search
                        placeholder="Search part number or description"
                        onSearch={handleSearch}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 300 }}
                        allowClear
                    />
                    <Select
                        placeholder="Filter by Make"
                        value={make}
                        onChange={(v) => { setMake(v); setPage(1); }}
                        allowClear
                        style={{ width: 180 }}
                    >
                        {stats?.parts_by_make.map(m => (
                            <Option key={m.make} value={m.make}>{m.make} ({m.count})</Option>
                        ))}
                    </Select>
                </Space>

                <Table
                    columns={columns}
                    dataSource={parts}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total: total,
                        onChange: (p) => setPage(p),
                        showSizeChanger: false
                    }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default SharedPartsBrowser;
